// appVersion — B-1b „Wersje i flota" (MONIT 2026-09-12_1750, F3): wersja skorupy + czysta
// logika stanów kontroli wersji (a–e). Bez Reacta, bez IDB, bez sieci — testowalne w node:test.
//
// APP_VERSION: stała wstrzykiwana przy buildzie przez vite.config.js (`define`), równa
// `const VERSION = 'vNN'` z public/sw.js (jedno źródło prawdy). W node:test / dev bez builda
// = 'dev'.
//
// Stany F3 (decyzje monitu; ZASADA TWARDA: nigdy nie blokować technika, który nie ma jeszcze
// kompletu plików nowej wersji w telefonie):
//   (a) skorupa == aktywny SW == latest → nic
//   (b) nowa wersja zainstalowana W KOMPLECIE (SW aktywny/waiting nowszy niż skorupa) →
//       baner „🔄 Nowa wersja gotowa" + „Przeładuj"
//   (c) pobieranie w toku (installing albo latest > skorupa bez kompletu) → cichy baner
//       „⬇ Pobieram nową wersję n/N, dokończy się przy zasięgu"; PRACA DOZWOLONA
//   (d) skorupa < min_required I nowa wersja w komplecie → pełnoekranowa blokada z JEDNYM
//       przyciskiem „Przeładuj" (działa bez internetu, pliki już są w telefonie)
//   (e) skorupa < min_required I NIE w komplecie → jak (c) z mocniejszym tekstem; PRACA DOZWOLONA
//
// „W komplecie" = install nowej wersji SW zakończył się sukcesem (cache.addAll all-or-nothing),
// czyli: registration.waiting istnieje z wersją > skorupa ALBO kontroler (aktywny SW) ma
// wersję > skorupa (skipWaiting + clients.claim w v72+ przejmują otwartą stronę).

import { t } from './i18n.js';

/* global __APP_VERSION__ */
export const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__ ? String(__APP_VERSION__) : 'dev';

export const VERSION_STATES = Object.freeze(['a', 'b', 'c', 'd', 'e']);
/** DN3 = (a): zamknięty baner „Nowa wersja gotowa" wraca po 1 h. */
export const BANNER_DISMISS_MS = 60 * 60 * 1000;

/** `v72` → 72; inne → null. */
export function parseVersion(v) {
  if (typeof v !== 'string') return null;
  const m = /^v(\d{1,5})$/.exec(v.trim());
  return m ? Number(m[1]) : null;
}

/** Porównanie liczbowe wersji `vNN`; nieznana traktowana jako najniższa. */
export function compareVersions(a, b) {
  const x = parseVersion(a); const y = parseVersion(b);
  if (x == null && y == null) return 0;
  if (x == null) return -1;
  if (y == null) return 1;
  return x === y ? 0 : (x < y ? -1 : 1);
}

function newer(v, than) {
  return parseVersion(v) != null && compareVersions(v, than) > 0;
}

/**
 * Ocena stanu kontroli wersji.
 * @param {Object} p
 * @param {string} p.shell             wersja skorupy (APP_VERSION)
 * @param {{version?:string, precached?:number|null, expected?:number|null}|null} [p.active]
 *        kontroler (aktywny SW) wg GET_VERSION; null = brak kontrolera (pierwsze otwarcie/desktop)
 * @param {{version?:string, precached?:number|null, expected?:number|null}|null} [p.installing]
 *        registration.installing (worker w trakcie precache)
 * @param {{version?:string}|null} [p.waiting]   registration.waiting (zainstalowany, czeka)
 * @param {string|null} [p.latest]                z serwera (puls / GET /api/version); null offline
 * @param {string|null} [p.minRequired]           z serwera; null = brak blokady
 * @returns {{state:'a'|'b'|'c'|'d'|'e', shell:string, target:string|null, complete:boolean,
 *            downloading:boolean, belowMin:boolean, progress:{n:number,N:number|null}|null,
 *            latest:string|null, minRequired:string|null}}
 */
export function evaluateVersionState({ shell, active = null, installing = null, waiting = null, latest = null, minRequired = null } = {}) {
  const sh = typeof shell === 'string' && shell ? shell : 'dev';
  const activeNewer = !!(active && newer(active.version, sh));
  const waitingNewer = !!(waiting && newer(waiting.version, sh));
  // Komplet = install nowej wersji zakończony (waiting) albo nowa wersja już aktywna.
  const complete = activeNewer || waitingNewer;
  const latestNewer = newer(latest, sh);
  const downloading = !complete && (!!installing || latestNewer);
  const belowMin = !!(minRequired && parseVersion(sh) != null && compareVersions(sh, minRequired) < 0);

  let target = null;
  if (waitingNewer) target = waiting.version;
  else if (activeNewer) target = active.version;
  else if (installing?.version && newer(installing.version, sh)) target = installing.version;
  else if (latestNewer) target = latest;
  else if (installing?.version) target = installing.version;

  const progress = installing
    ? { n: Number(installing.precached) || 0, N: installing.expected != null ? Number(installing.expected) : null }
    : null;

  let state = 'a';
  if (belowMin && complete) state = 'd';
  else if (belowMin && !complete) state = 'e';
  else if (complete) state = 'b';
  else if (downloading) state = 'c';

  return { state, shell: sh, target, complete, downloading, belowMin, progress, latest: latest || null, minRequired: minRequired || null };
}

/** Czy zamknięty baner (b) ma wrócić: po BANNER_DISMISS_MS od zamknięcia (DN3 = a). */
export function isBannerDismissed(dismissedAt, now = Date.now()) {
  if (!dismissedAt) return false;
  return now - dismissedAt < BANNER_DISMISS_MS;
}

/** Tekst banera/blokady dla stanu (jedno miejsce prawdy dla UI i testów). */
export function versionStateText(v) {
  if (!v) return null;
  const tg = v.target ? ` ${v.target}` : '';
  const p = v.progress && v.progress.N ? ` ${v.progress.n}/${v.progress.N}` : '';
  switch (v.state) {
    case 'b': return t('ver.ready', { t: tg });
    case 'c': return t('ver.downloading', { t: tg, p });
    case 'd': return t('ver.required', { t: tg });
    case 'e': return t('ver.requiredPartial', { t: tg, p });
    default: return null;
  }
}
